<template>
	<VDropdown
		:triggers="[]"
		:shown="isOpen"
		popper-class="app-confirmation-popover__popover"
		:distance="5"
		@auto-hide="onCancel"
	>
		<slot :open-confirmation="openConfirmation" />

		<template #popper>
			<SuiMessage
				v-if="isOpen"
				id="confirmation"
				:title="confirmationTitle"
				:type="type"
				:close-label="$t('close')"
				:buttons="confirmationButtons"
				class="app-confirmation-popover__message"
				disable-animations
				@close="closeConfirmation"
			>
				<AppTranslatedTextWithHTML
					:id="confirmationMessage"
					:values="messageValue"
				/>
			</SuiMessage>
		</template>
	</VDropdown>
</template>

<script setup lang="ts">
import { SuiMessageTypeType, SuiMessage } from '@developers/scoro-ui';
import { ref, computed, toRefs } from 'vue';
import { useTranslation } from '@/composables';
import AppTranslatedTextWithHTML from '../App/AppTranslatedTextWithHTML.vue';

export type AppConfirmationPopoverProps = {
	type?: SuiMessageTypeType;
	cancelOnClose?: boolean;
	isInitiallyOpen?: boolean;
	title?: string;
	message?: string;
	messageValue?: Record<string, string>;
	primaryButtonLabel?: string;
	secondaryButtonLabel?: string;
};

const props = withDefaults(defineProps<AppConfirmationPopoverProps>(), {
	type: SuiMessageTypeType.Warning,
	cancelOnClose: false,
	isInitiallyOpen: false,
	title: undefined,
	message: undefined,
	messageValue: undefined,
	primaryButtonLabel: undefined,
	secondaryButtonLabel: undefined,
});

const emit = defineEmits(['confirm', 'cancel']);

const { t } = useTranslation();

const isOpen = ref(props.isInitiallyOpen);

const { title, message, primaryButtonLabel, secondaryButtonLabel } =
	toRefs(props);

const confirmationTitle = computed(
	() => title.value ?? t('general_delete_confirmation_title')
);

const confirmationMessage = computed(
	() => message.value ?? t('general_delete_confirmation_message')
);

const primaryLabel = computed(() => primaryButtonLabel.value ?? t('yes'));

const secondaryLabel = computed(() => secondaryButtonLabel.value ?? t('no'));

let payloadToConfirm: unknown;

const openConfirmation = (payload?: unknown) => {
	payloadToConfirm = payload;
	isOpen.value = true;
};

const closeConfirmation = () => {
	isOpen.value = false;

	if (props.cancelOnClose) {
		emit('cancel');
	}
};

const onCancel = () => {
	isOpen.value = false;
	emit('cancel');
};

const confirmationButtons = computed(() => [
	{
		label: primaryLabel.value,
		isPrimary: true,
		callback: () => {
			closeConfirmation();
			emit('confirm', payloadToConfirm);
		},
	},
	{
		label: secondaryLabel.value,
		isPrimary: false,
		callback: onCancel,
	},
]);
</script>

<style lang="scss" scoped>
.app-confirmation-popover {
	&__message {
		--sui-message-margin: 0;
		--sui-message-box-shadow: none;

		max-width: 80rem;
	}
}
</style>

<style lang="scss">
.app-confirmation-popover__popover .v-popper__inner.v-popper__inner {
	padding: 0;
}
</style>
